import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { Table } from '../../components/ui/Table';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Modal } from '../../components/ui/Modal/Modal';
import { Input } from '../../components/ui/Input/Input';
import { apiFetch } from '@/lib/api';

interface UseCase {
  id: string;
  label: string;
}

function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');
}

export function OrgsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [orgs, setOrgs] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);

  // Fetch use cases for label lookup
  const { data: useCases } = useQuery<UseCase[]>({
    queryKey: ['admin', 'use-cases'],
    queryFn: async () => {
      const res = await apiFetch('/api/admin/use-cases');
      if (!res.ok) return [];
      const json = await res.json();
      return json.success ? json.data : [];
    },
  });

  // Create Org modal state
  const [showCreateModal, setShowCreateModal] = React.useState(false);
  const [createName, setCreateName] = React.useState('');
  const [createLoading, setCreateLoading] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/admin/orgs', { credentials: 'include' });
        const json = await res.json();
        if (!json.success) throw new Error(json.error?.message || 'Failed to load');

        // Fetch member counts for each org
        const orgsWithCounts = await Promise.all(
          (json.data || []).map(async (org: any) => {
            try {
              const detailRes = await fetch(`/api/admin/orgs/${org.id}`, { credentials: 'include' });
              const detailJson = await detailRes.json();
              return {
                ...org,
                memberCount: detailJson.success ? detailJson.data.memberCount : 0
              };
            } catch {
              return { ...org, memberCount: 0 };
            }
          })
        );

        setOrgs(orgsWithCounts);
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleCreateOrg = async () => {
    if (!createName.trim()) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const res = await fetch('/api/admin/orgs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name: createName.trim() }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to create organization');
      setShowCreateModal(false);
      setCreateName('');
      navigate(`/admin/orgs/${json.data.id}`);
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = [
    {
      key: 'name',
      title: 'Organization',
      sortable: true,
      render: (value: string, record: any) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{record.slug}</div>
        </div>
      )
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (value: string) => {
        const variant = value === 'active' ? 'success' : value === 'suspended' ? 'error' : 'warning';
        return <Badge variant={variant}>{value}</Badge>;
      }
    },
    {
      key: 'plan_key',
      title: 'Plan',
      render: (_: any, record: any) => {
        const billing = record.billing;
        if (!billing) return <span className="text-sm text-gray-500">—</span>;
        const badgeVariant = billing.status === 'active' || billing.status === 'trialing' ? 'success' : 'warning';
        return (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{billing.planName || '—'}</div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <code className="bg-gray-100 px-1.5 py-0.5 rounded">{billing.planKey || 'n/a'}</code>
              {billing.status && <Badge variant={badgeVariant}>{billing.status}</Badge>}
            </div>
          </div>
        );
      }
    },
    {
      key: 'memberCount',
      title: 'Members',
      sortable: true,
      align: 'center' as const,
      render: (value: number) => (
        <span className="font-medium">{value}</span>
      )
    },
    {
      key: 'use_cases',
      title: 'Use Cases',
      render: (value: string[] | null) => {
        if (!value || value.length === 0) {
          return <span className="text-sm text-gray-400">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1 max-w-xs">
            {value.slice(0, 3).map((ucId) => {
              const uc = useCases?.find(u => u.id === ucId);
              return (
                <Badge key={ucId} variant="outline">
                  {uc?.label || ucId}
                </Badge>
              );
            })}
            {value.length > 3 && (
              <Badge variant="secondary">+{value.length - 3}</Badge>
            )}
          </div>
        );
      }
    },
    {
      key: 'next_payment',
      title: 'Next Payment',
      render: (_: any, record: any) => {
        const billing = record.billing;
        const next = billing?.nextPaymentAttemptAt || billing?.currentPeriodEnd;
        return next ? (
          <div>
            <div className="text-sm text-gray-900">{new Date(next).toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{new Date(next).toLocaleTimeString()}</div>
          </div>
        ) : (
          <span className="text-sm text-gray-500">—</span>
        );
      }
    },
    {
      key: 'created_at',
      title: 'Created',
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      align: 'right' as const,
      render: (_: any, record: any) => (
        <div className="flex gap-2">
          <Link to={`/admin/orgs/${record.id}`}>
            <Button size="sm" variant="outline">View Details</Button>
          </Link>
        </div>
      )
    }
  ];

  const filters = [
    {
      key: 'status',
      label: 'Filter by Status',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Suspended', value: 'suspended' },
        { label: 'Trialing', value: 'trialing' }
      ]
    }
  ];

  return (
    <PageContainer>
      <PageHeader
        title="Organizations"
        description="Manage all organizations on the platform"
      >
        <Button onClick={() => setShowCreateModal(true)}>
          Create Organization
        </Button>
      </PageHeader>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={orgs}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search organizations..."
        filters={filters}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: [5, 10, 20, 50]
        }}
        emptyText="No organizations found."
      />

      <Modal
        isOpen={showCreateModal}
        onClose={() => { setShowCreateModal(false); setCreateName(''); setCreateError(null); }}
        title="Create Organization"
        size="sm"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => { setShowCreateModal(false); setCreateName(''); setCreateError(null); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateOrg} isLoading={createLoading} disabled={!createName.trim()}>
              Create
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Organization Name"
            placeholder="e.g. Acme Corp"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreateOrg(); }}
          />
          {createName.trim() && (
            <p className="text-sm text-gray-500">
              Slug: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{slugify(createName)}</code>
            </p>
          )}
          {createError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{createError}</p>
            </div>
          )}
        </div>
      </Modal>
    </PageContainer>
  );
}
